// Copyright (c) 2020 WiseTime. All rights reserved.

import { DateTime, Duration } from "luxon"
import * as R from "ramda"
import { range } from "ramda"
import { DataRetentionPeriod } from "../../generated/wisetime/type/data_retention_pb"
import { Date as PbDate, DateSchema } from "../../generated/google/type/date_pb"
import { DaySummary, DaySummary_TimePostedToSite } from "../../generated/wisetime/user/timeline/v4/timeline_pb"
import { create } from "@bufbuild/protobuf"

// Format yyyyMMddHH, usually in UTC
export type TimelineHour = number
// Format yyyyMMdd, usually in local time zone
export type TimelineDate = number
// Format: yyyyMM in local time zone
export type TimelineMonth = number

export type ActiveTrashMonthAndDate = {
  activeTrashMonth: TimelineMonth,
  // set to scroll view to the particular day tab on trash page
  activeTrashDate?: TimelineDate | undefined,
}

// Format yyyy-MM-dd. Used for URL path readability purpose
export type TimelinePathDate = string
// Format yyyy-MM. Used for URL path readability purpose
export type TimelineTrashPathDate = string

export type Clock = () => DateTime

export type ZonedDateRange = {
  startDateLocal: TimelineDate,
  endDateLocal: TimelineDate, // Inclusive.
  timezone: number,
}

export type TimeRange = {
  startActivityHour: TimelineHour,
  endActivityHour: TimelineHour,
}

export type DateRange = {
  startDateLocal: TimelineDate,
  endDateLocal: TimelineDate, // Inclusive.
}

export type MonthRange = {
  startMonth: TimelineMonth,
  endMonth: TimelineMonth, // Inclusive
}

const TIMELINE_PATH_DATE_FORMAT = "yyyy-MM-dd"
const TIMELINE_TRASH_PATH_DATE_WITH_DAY_FORMAT = "yyyy-MM#dd"
const TIMELINE_TRASH_PATH_DATE_FORMAT = "yyyy-MM"
export const TIMELINE_TRASH_MONTH_TAB_COUNT = 6 // months in a half year
export const TODAY_DATE_PARAM = "today"

export const toMonthDate = (date: DateTime): TimelineMonth => {
  return Number(date.toFormat("yyyyMM"))
}

export const toTimelineHourUtc = (date: DateTime): TimelineHour =>
  Number(date.toUTC().toFormat("yyyyMMddHH"))

export const getCurrentDate = (): TimelineDate => {
  return toTimelineDate(DateTime.local())
}

export const getCurrentMonth = (): TimelineMonth => {
  return toMonthDate(DateTime.local())
}

export const isFutureDate = (date: TimelineDate): boolean => (
  isFuture(DateTime.fromFormat(date.toString(), "yyyyMMdd"))
)

export const isFutureMonth = (date: TimelineMonth): boolean => (
  isFuture(DateTime.fromFormat(date.toString(), "yyyyMM"))
)

export const getTimelineUtcHoursForDay = (localDate: TimelineDate): TimelineHour[] =>
  range(0, 24).map(localHour => {
    const localDateTimeOfHour = DateTime.fromFormat(localDate.toString(), "yyyyMMdd").set({
      hour: localHour,
      minute: 0,
      second: 0
    })
    return toTimelineHourUtc(localDateTimeOfHour)
  })

export const getDateStartHourUtc = (date: DateTime): TimelineHour => {
  return toTimelineHourUtc(date.set({ hour: 0, minute: 0, second: 0, millisecond: 0 }))
}

export const getDateEndHourUtc = (date: DateTime): TimelineHour => {
  return toTimelineHourUtc(date.set({ hour: 23, minute: 0, second: 0, millisecond: 0 }))
}

export const getMonthStartHourUtc = (date: DateTime): TimelineHour => {
  return toTimelineHourUtc(date.set({ day: 1, hour: 0, minute: 0, second: 0, millisecond: 0 }))
}

export const getMonthEndHourUtc = (date: DateTime): TimelineHour => {
  return toTimelineHourUtc(date.set({ day: date.daysInMonth, hour: 23, minute: 0, second: 0, millisecond: 0 }))
}

export const isFuture = (date: DateTime): boolean => {
  return date.diffNow("days").days > 0
}

/**
 * Converts an activity hour in UTC with time zone offset into a local activity hour.
 *
 * @param activityHourUtc The activity hour in UTC with format 'yyyyMMddHH'
 * @param timeZoneOffset Time zone offset from UTC in hours.
 */
export const getActivityHourLocal = (activityHourUtc: TimelineHour, timeZoneOffset: number): TimelineHour => {
  const activityHourUtcStr = activityHourUtc.toString(10)
  const yearUtc = Number(activityHourUtcStr.substring(0, 4))
  const monthUtc = Number(activityHourUtcStr.substring(4, 6))
  const dateUtc = Number(activityHourUtcStr.substring(6, 8))
  const hourUtc = Number(activityHourUtcStr.substring(8, 10))
  return Number(
    DateTime.utc(yearUtc, monthUtc, dateUtc, hourUtc, 0, 0, 0)
      .plus({ hours: timeZoneOffset }).toFormat("yyyyMMddHH")
  )
}

export const getActivityHourLabel = (date: DateTime, timezoneMinuteOffset: number): string => {
  const localDate = date.toLocal()
  const start = localDate.plus({ minutes: timezoneMinuteOffset })
  const end = start.plus({ minutes: 59 })
  return `${start.toLocaleString(DateTime.TIME_SIMPLE)} - ${end.toLocaleString(DateTime.TIME_SIMPLE)}`
}

export const getBlockHourPeriodLabel = (activityHourLocal: number, timezoneMinuteOffset: number): string => {
  const activityHourLocalStr = activityHourLocal.toString(10)
  return getActivityHourLabel(DateTime.fromFormat(activityHourLocalStr, "yyyyMMddHH"), timezoneMinuteOffset)
}

export const zeroDuration = "0m"

export type FormattedBlockTime = {
  hours: number,
  minutes: number,
}

const addBlockTimes = (
  firstBlockTime: FormattedBlockTime,
  secondBlockTime: FormattedBlockTime,
): FormattedBlockTime => {
  let hours = firstBlockTime.hours + secondBlockTime.hours
  let minutes = firstBlockTime.minutes + secondBlockTime.minutes
  if (minutes >= 60) {
    const extraHours = Math.floor(minutes / 60)
    hours += extraHours
    minutes = minutes - extraHours * 60
  }
  return { hours, minutes }
}

const minusBlockTimes = (
  firstBlockTime: FormattedBlockTime,
  secondBlockTime: FormattedBlockTime,
): FormattedBlockTime => {
  let hours = firstBlockTime.hours - secondBlockTime.hours
  let minutes = firstBlockTime.minutes - secondBlockTime.minutes
  if (minutes < 0 && hours > 0) {
    hours -= 1
    minutes += 60
  }
  return { hours, minutes }
}

const toFormattedTimeStr = (blockTime: FormattedBlockTime) => {
  let durationStr = ""
  if (blockTime.minutes > 0) {
    durationStr = blockTime.minutes.toString() + "m"
  }
  if (blockTime.hours > 0) {
    durationStr = blockTime.hours.toString() + "h" + durationStr
  }
  return durationStr || zeroDuration
}

const formatBlockDuration = (seconds: number): FormattedBlockTime => {
  let hours = Math.floor(seconds / 60 / 60)
  let minutes = Math.ceil(seconds / 60) - (hours * 60)

  // for seconds close to multiple of hour seconds (3600) : e.g. 3570
  if (minutes === 60) {
    hours += 1
    minutes = 0
  }

  return { hours, minutes }
}

export const getBlockDurationStr = (seconds: number): string =>
  toFormattedTimeStr(formatBlockDuration(seconds))

export const getRowsDurationStr = (seconds: number): string => {
  const hoursPart = Math.floor(seconds / 60 / 60)
  const minutesPart = Math.floor(seconds / 60) - (hoursPart * 60)
  const secondsPart = seconds % 60

  let durationStr = ""
  if (hoursPart > 0) {
    durationStr += hoursPart.toString().padStart(2, "0") + ":"
  }
  durationStr += minutesPart.toString().padStart(2, "0") + ":"
  durationStr += secondsPart.toString().padStart(2, "0")
  return durationStr
}

export type FormattedTimePostedToSite = {
  siteId: string,
  posted: string,
}

export type FormattedTimeStats = {
  total: string,
  unposted: string,
  posted: string,
  postedToTeams: FormattedTimePostedToSite[],
}

export const computeTotalPostedSecs = (timePostedToSiteList: DaySummary_TimePostedToSite[] = []) =>
  timePostedToSiteList
    .map(daySummary => daySummary.totalPosted)
    .reduce((result, duration) => result + Number(duration?.seconds ?? 0), 0)

export const formatTimeStats = (
  daySummary: DaySummary | undefined,
): FormattedTimeStats => {
  const totalPostedSecs = computeTotalPostedSecs(daySummary?.timePostedToSite)
  const totalSecs = totalPostedSecs + Number((daySummary?.totalUnposted?.seconds ?? 0))
  const timePostedToSiteList = daySummary?.timePostedToSite ?? []
  const formattedTotal = formatBlockDuration(totalSecs)
  const formattedUnposted = formatBlockDuration(totalSecs - totalPostedSecs)
  const formattedPosted = minusBlockTimes(formattedTotal, formattedUnposted)
  let currentSum: FormattedBlockTime = { hours: 0, minutes: 0 }
  const postedToTeams = timePostedToSiteList.map(({ siteId, totalPosted }, index, arr) => {
    if (index < arr.length - 1) {
      const formattedPostedToTeam = formatBlockDuration(Number(totalPosted?.seconds ?? 0))
      currentSum = addBlockTimes(currentSum, formattedPostedToTeam)
      return ({
        siteId,
        posted: toFormattedTimeStr(formattedPostedToTeam),
      })
    }
    const formattedPostedToTeam = minusBlockTimes(formattedPosted, currentSum)
    return ({
      siteId,
      posted: toFormattedTimeStr(formattedPostedToTeam),
    })
  })
  return {
    total: toFormattedTimeStr(formattedTotal),
    posted: toFormattedTimeStr(formattedPosted),
    unposted: toFormattedTimeStr(formattedUnposted),
    postedToTeams,
  }
}

// Format: HH:MM:SS
type MaskedDuration = string

export const getMaskedDuration = (totalTime: string): MaskedDuration => {
  const mask = "00:00:00"
  return `${mask.slice(0, (mask.length - totalTime.length))}${totalTime}`
}

export const getSecondsFromMaskedDuration = (maskedDuration: MaskedDuration): number => {
  const [
    hours,
    minutes,
    seconds,
  ] = maskedDuration.split(":").map((timePeriod: string): number => Number(timePeriod))
  return ((hours ?? 0) * 60 * 60) + ((minutes ?? 0) * 60) + (seconds ?? 0)
}

export const toDate = (date: TimelineDate): DateTime => {
  return DateTime.fromFormat(date.toString(), "yyyyMMdd").set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0
  })
}

export const toTimelineDate = (date: DateTime): TimelineDate =>
  Number(date.toFormat("yyyyMMdd"))

export const convertMonthDateToDateTime = (month: TimelineMonth): DateTime => {
  return DateTime.fromFormat(month.toString(), "yyyyMM").set({
    day: 1,
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0
  })
}

export const convertTimelinePathDateToDateTime = (date: TimelinePathDate): DateTime => {
  return DateTime.fromFormat(date.toString(), TIMELINE_PATH_DATE_FORMAT).set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0
  })
}

export const convertTimelineTrashPathDateToDateTime = (date: TimelineTrashPathDate): DateTime => {
  return DateTime.fromFormat(date.toString(), TIMELINE_TRASH_PATH_DATE_FORMAT).set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0
  })
}

export const convertTimeLineTrashPathDateWithDayToDateTime = (date: TimelineTrashPathDate): DateTime => {
  return DateTime.fromFormat(date.toString(), TIMELINE_TRASH_PATH_DATE_WITH_DAY_FORMAT).set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0
  })
}

/**
 * Converts an activity hour in UTC with time zone offset into TimeLineDate.
 * @param utcHour The activity hour in UTC with format 'yyyyMMddHH'
 * @param timeZoneOffset Time zone offset from UTC in hours.
 *
 */

export const getLocalTimelineDateFromUTCHour = (utcHour: TimelineHour, timeZoneOffset: number): TimelineDate => {
  const activityHourUtcStr = utcHour.toString(10)
  const yearUtc = Number(activityHourUtcStr.substring(0, 4))
  const monthUtc = Number(activityHourUtcStr.substring(4, 6))
  const dateUtc = Number(activityHourUtcStr.substring(6, 8))
  const hourUtc = Number(activityHourUtcStr.substring(8, 10))
  /**
   *  For timezones having timeOffset in decimals e.g. '5.75' for NPT the minutes are left to be added while converting
   *  UTC time to activity hours.
   *  @see getActivityHourLocal
   *  That's why we need to add minutes calculated from the decimal value (e.g. '.75' for NPT)
   */
  return Number(
    DateTime.utc(yearUtc, monthUtc, dateUtc, hourUtc, 0, 0, 0)
      .plus({ hours: timeZoneOffset, minutes: getTimezoneMinutesOffset(timeZoneOffset) }).toFormat("yyyyMMdd")
  )
}

// Generate the list of activity hours in the given range (inclusive)
export const generateActivityHoursInRange = (start: TimelineHour, end: TimelineHour): TimelineHour[] => {
  const startDate = DateTime.fromFormat(start.toString(), "yyyyMMddHH")
  const endDate = DateTime.fromFormat(end.toString(), "yyyyMMddHH")
  const hours = []
  let current = startDate
  while (current.toMillis() <= endDate.toMillis()) {
    hours.push(Number(current.toFormat("yyyyMMddHH")))
    current = current.plus({ hours: 1 })
  }
  return hours
}

export const getDatesOfWeek = (date: DateTime): TimelineDate[] => {
  const currentDayOfWeek = date.weekday // 1 is Monday and 7 is Sunday
  let firstDateOfWeek: DateTime
  if (currentDayOfWeek === 1) {
    firstDateOfWeek = date
  } else {
    const distance = currentDayOfWeek - 1
    firstDateOfWeek = date.minus({ days: distance })
  }

  const datesOfWeek: TimelineDate[] = [toTimelineDate(firstDateOfWeek)]
  for (let i = 0; i < 6; i++) {
    const dateOfWeek = firstDateOfWeek.plus({ days: i + 1 })
    datesOfWeek.push(toTimelineDate(dateOfWeek))
  }
  return datesOfWeek
}

export const getWeekRange = (date: DateTime): DateRange => {
  const weekDates = getDatesOfWeek(date)
  return {
    startDateLocal: weekDates[0]!,
    endDateLocal: weekDates[6]!
  }
}

export const getTimelineDatesInRange = (start: TimelineDate, end: TimelineDate): TimelineDate[] => {
  if (start > end) {
    throw new Error(
      "getTimelineDatesInRange. The start date of the range - "
      + start
      + " is after end date "
      + end
    )
  }
  const dates: DateTime[] = []
  let currentDate = toDate(start)
  const endDate = toDate(end)
  while (currentDate <= endDate) {
    dates.push(currentDate)
    currentDate = currentDate.plus({ days: 1 })
  }
  return dates.map(d => toTimelineDate(d))
}

export const splitDateRange = (start: TimelineDate, end: TimelineDate, maxDays: number): DateRange[] => {
  const startDate = toDate(start)
  const endDate = toDate(end)

  const splitRanges: DateRange[] = []
  let current = startDate
  while (current.toMillis() < endDate.toMillis()) {
    if (current > startDate) {
      const lastRange = [...splitRanges].pop()
      if (lastRange) {
        const currentRangeStart = toDate(lastRange.endDateLocal).plus({ days: 1 })
        splitRanges.push({ startDateLocal: toTimelineDate(currentRangeStart), endDateLocal: toTimelineDate(current) })
      } else {
        splitRanges.push({ startDateLocal: start, endDateLocal: toTimelineDate(current) })
      }
    }
    current = current.plus({ days: maxDays })
  }

  const lastRange = [...splitRanges].pop()
  if (lastRange) {
    const lastRangeEnd = lastRange ? lastRange.endDateLocal : start
    const lastRangeEndDate = toDate(lastRangeEnd)
    if (lastRangeEndDate.toMillis() < endDate.toMillis()) {
      splitRanges.push({
        startDateLocal: toTimelineDate(lastRangeEndDate.plus({ days: 1 })),
        endDateLocal: toTimelineDate(endDate)
      })
    }
  } else {
    splitRanges.push({ startDateLocal: start, endDateLocal: end })
  }
  return splitRanges
}

export const getHalfYearRange = (date: DateTime): MonthRange => {
  const halfYearMonths = getMonthDatesForHalfYear(date)
  return {
    startMonth: halfYearMonths[0]!,
    endMonth: halfYearMonths[TIMELINE_TRASH_MONTH_TAB_COUNT - 1]!
  }
}

export const getMonthDatesForHalfYear = (date: DateTime): TimelineMonth[] => {
  const currentMonthOfYear = date.month
  const currentHalfYearIndex = currentMonthOfYear % TIMELINE_TRASH_MONTH_TAB_COUNT
  const endIndexForEndMonth = Math.floor(currentMonthOfYear / TIMELINE_TRASH_MONTH_TAB_COUNT) // 1 for June 2 for December
  let firstMonthOfMidYear: DateTime
  if (currentHalfYearIndex === 1) {
    firstMonthOfMidYear = date
  } else if (currentHalfYearIndex === 0 && endIndexForEndMonth > 0) {
    firstMonthOfMidYear = date.minus({ months: TIMELINE_TRASH_MONTH_TAB_COUNT - 1 })
  } else {
    const distance = currentHalfYearIndex - 1
    firstMonthOfMidYear = date.minus({ months: distance })
  }
  const monthDates: DateTime[] = [firstMonthOfMidYear.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })]
  for (let i = 0; i < 5; i++) {
    const previousMonthDate = firstMonthOfMidYear.plus({ months: i + 1 }).set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0
    })
    monthDates.push(previousMonthDate)
  }
  return monthDates.map(monthDate => toMonthDate(monthDate))
}

export type ActivityHourInfo = {
  date: DateTime,
  activityHour: TimelineHour,

  // Number of seconds of the current activity hour elapsed so far.
  // Usually it is equal to 3600, but if the given activity hour is
  // equal to the current activity hour, it will be < 3600
  secondsElapsed: number,
}

export const getActivityHoursOfDay = (date: DateTime): ActivityHourInfo[] => {
  const now = DateTime.local()

  // Generate new dates until midnight or current time are reached
  const activityHours: ActivityHourInfo[] = []

  let current = DateTime.local(date.year, date.month, date.day, 0, 0, 0)
  // in timezone without DST 24 hours, in timezone with DST hours count can be 23 or 25 on days when the shift happens
  const nextDay = DateTime.local(date.year, date.month, date.day, 0, 0, 0).plus({ days: 1 })
  for (let hour = 0; current.toMillis() < now.toMillis() && current.toMillis() < nextDay.toMillis() && hour < 25; hour++) {
    const nextHour = current.plus({ hours: 1 })
    const secondsSoFar = Math.round((Math.min(nextHour.toMillis(), now.toMillis()) - current.toMillis()) / 1000)
    activityHours.push({
      date: current,
      secondsElapsed: secondsSoFar,
      activityHour: toTimelineHourUtc(current),
    })
    current = nextHour
  }

  return activityHours
}

export const getFullDateLabelWithWeekDay = (
  activityDayLocalCurrentTz: number,
  locale = navigator.language,
): string => {
  const date = DateTime.fromFormat(activityDayLocalCurrentTz.toString(), "yyyyMMdd")
  return date.setLocale(locale).toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)
}

export const getMonthYearLabel = (
  date: DateTime,
  locale = navigator.language,
): string => {
  return date.setLocale(locale).toLocaleString({ month: "long", year: "numeric" })
}

export const isValidTimelinePathDate = (timeLinePathDateStr: string): boolean => {
  return timeLinePathDateStr === TODAY_DATE_PARAM
    || DateTime.fromFormat(timeLinePathDateStr, TIMELINE_PATH_DATE_FORMAT).isValid
}

export const isValidTimelineTrashPathDate = (timeLinePathDateStr: string): boolean => {
  return DateTime.fromFormat(timeLinePathDateStr, TIMELINE_TRASH_PATH_DATE_FORMAT).isValid
}

export const isValidTimelineTrashPathDateWithDay = (timeLinePathDateStr: string): boolean => {
  return DateTime.fromFormat(timeLinePathDateStr, TIMELINE_TRASH_PATH_DATE_WITH_DAY_FORMAT).isValid
}

export const convertTimelineDateToTimelinePathDate = (timelineDate: TimelineDate): TimelinePathDate => {
  return toDate(timelineDate).toFormat(TIMELINE_PATH_DATE_FORMAT)
}

export const convertTimelineDateToTimelineTrashPathDateWithDay = (timelineDate: TimelineDate): TimelinePathDate => {
  return toDate(timelineDate).toFormat(TIMELINE_TRASH_PATH_DATE_WITH_DAY_FORMAT)
}

export const convertTimelinePathDateToTimelineDate = (timelinePathDate: TimelinePathDate): TimelineDate => {
  return toTimelineDate(convertTimelinePathDateToDateTime(timelinePathDate))
}

export const convertTimelineTrashPathDateWithDayToTimeLineDate = (timeLinePathDateStr: string): TimelineDate => {
  return toTimelineDate(convertTimeLineTrashPathDateWithDayToDateTime(timeLinePathDateStr))
}

export const convertTimelineTrashPathDateToTimelineMonth = (timelineTrashPathDate: TimelineTrashPathDate): TimelineMonth => {
  return toMonthDate(convertTimelineTrashPathDateToDateTime(timelineTrashPathDate))
}

export const convertTimeLineMonthToTimelineTrashPathDate = (timelineMonth: TimelineMonth): TimelineTrashPathDate => {
  return convertMonthDateToDateTime(timelineMonth).toFormat(TIMELINE_TRASH_PATH_DATE_FORMAT)
}

export const getTimezoneMinutesOffset = (timeZoneOffset: number): number => {
  return Math.round((timeZoneOffset % 1) * 60)
}

export const timeRangeUtcForDate = (date: TimelineDate): TimeRange => {
  const dateTime = toDate(date)
  return {
    startActivityHour: getDateStartHourUtc(dateTime),
    endActivityHour: getDateEndHourUtc(dateTime),
  }
}

export const endHourUtcOfPreviousDay = (date: TimelineDate): TimelineHour =>
  getDateEndHourUtc(toDate(date).minus({ days: 1 }))

export const startHourUtcOfNextDay = (date: TimelineDate): TimelineHour =>
  getDateStartHourUtc(toDate(date).plus({ days: 1 }))

export const getActiveTrashMonthAndDateFromPath = (path: string): ActiveTrashMonthAndDate => {
  if (isValidTimelineTrashPathDateWithDay(path)) {
    const activeTrashDate = convertTimelineTrashPathDateWithDayToTimeLineDate(path)
    return {
      activeTrashMonth: toMonthDate(toDate(activeTrashDate)),
      activeTrashDate,
    }
  }
  if (isValidTimelineTrashPathDate(path)) {
    return {
      activeTrashMonth: convertTimelineTrashPathDateToTimelineMonth(path),
      activeTrashDate: undefined,
    }
  }
  return {
    activeTrashMonth: getCurrentMonth(),
    activeTrashDate: getCurrentDate(),
  }
}

type TimelineDateRange = {
  start: TimelineDate,
  end: TimelineDate,
}

export const getTilesRange = (date: DateTime = DateTime.local(), years = 2): TimelineDateRange => ({
  start: toTimelineDate(date.minus({ years })),
  end: toTimelineDate(date),
})

export const endOfWeekTimelineDate = (tilesRangeEnd: TimelineDate): TimelineDate => {
  return toTimelineDate(toDate(tilesRangeEnd).endOf("week"))
}

export const getRangeFromDataRetentionPeriod = (
  dataRetentionPeriod: DataRetentionPeriod = DataRetentionPeriod.FOURTEEN_DAYS,
  date: DateTime = DateTime.local(),
): TimelineDateRange => {
  const getStartDate = (): DateTime => {
    if (dataRetentionPeriod === DataRetentionPeriod.TWO_YEARS) {
      return date.minus({ years: 2 })
    }
    if (dataRetentionPeriod === DataRetentionPeriod.ONE_YEAR) {
      return date.minus({ years: 1 })
    }
    if (dataRetentionPeriod === DataRetentionPeriod.SIX_MONTHS) {
      return date.minus({ months: 6 })
    }
    if (dataRetentionPeriod === DataRetentionPeriod.THREE_MONTHS) {
      return date.minus({ months: 3 })
    }
    if (dataRetentionPeriod === DataRetentionPeriod.ONE_MONTH) {
      return date.minus({ months: 1 })
    }
    if (dataRetentionPeriod === DataRetentionPeriod.TWENTY_ONE_DAYS) {
      return date.minus({ days: 21 })
    }
    if (dataRetentionPeriod === DataRetentionPeriod.SEVEN_DAYS) {
      return date.minus({ days: 7 })
    }
    // defaults to DataRetentionPeriod.FOURTEEN_DAYS
    return date.minus({ days: 14 })
  }
  return ({
    start: toTimelineDate(getStartDate()),
    end: toTimelineDate(date),
  })
}

export const getDatesSortedByWeeks = (start: TimelineDate, end: TimelineDate): TimelineDate[] => {
  // include full weeks in range
  const dates = getTimelineDatesInRange(
    toTimelineDate(toDate(start).startOf("week")),
    toTimelineDate(toDate(end).endOf("week")),
  ).reverse()
  // sort by weeks in descending order and sort dates by days of the week
  return R.pipe(
    R.groupBy(
      (date: TimelineDate) => {
        const dateTime = DateTime.fromFormat(date.toString(), "yyyyMMdd")
        return `${dateTime.weekYear.toString()}-${dateTime.weekNumber.toString()}`
      },
    ),
    R.values,
    R.map((week: TimelineDate[]) => R.sort((a: TimelineDate, b: TimelineDate) => {
      const getDateMillis = (date: TimelineDate): number => DateTime.fromFormat(date.toString(), "yyyyMMdd").toMillis()
      return getDateMillis(a) - getDateMillis(b)
    })(week)),
    R.flatten,
  )(dates)
}

type YearAndMonth = {
  year: number,
  month: number,
}

export const getYearAndMonth = (date: TimelineDate): YearAndMonth => ({
  year: Number(date.toString().substring(0, 4)),
  month: Number(date.toString().substring(4, 6)),
})

type TimelineDateParts = {
  year: number,
  month: number,
  day: number,
}

export const getTimelineDateParts = (date: TimelineDate): TimelineDateParts => {
  const formattedDate = date.toString()
  return ({
    year: Number(formattedDate.substring(0, 4)),
    month: Number(formattedDate.substring(4, 6)),
    day: Number(formattedDate.substring(6, 8)),
  })
}

type ActivityHourParts = TimelineDateParts & {
  hour: number,
}

export const getActivityHourParts = (activityHour: number): ActivityHourParts => {
  const formattedActivityHour = activityHour.toString()
  return ({
    year: Number(formattedActivityHour.substring(0, 4)),
    month: Number(formattedActivityHour.substring(4, 6)),
    day: Number(formattedActivityHour.substring(6, 8)),
    hour: Number(formattedActivityHour.substring(8, 10)),
  })
}

type HoursOffset = number

export const getLocalTimezoneOffset = (activityHourUtc: number, timeZoneName: string): HoursOffset => {
  const {
    year,
    month,
    day,
    hour,
  } = getActivityHourParts(activityHourUtc)
  return DateTime.utc(year, month, day, hour).toLocal().setZone(timeZoneName).offset / 60
}

export const convertTimelineDateToPbDate = (timelineDate: TimelineDate): PbDate =>
  create(DateSchema, {
    day: Number(timelineDate.toString().substring(6, 8)),
    month: Number(timelineDate.toString().substring(4, 6)),
    year: Number(timelineDate.toString().substring(0, 4)),
  })

export const convertPbDateToTimelineDate = (date: PbDate): TimelineDate => toTimelineDate(DateTime.fromObject({
  day: date.day,
  month: date.month,
  year: date.year,
}))

export const convertDateToTimelineDate = (date: Date): TimelineDate => toTimelineDate(DateTime.fromObject({
  day: date.getDate(),
  month: date.getMonth() + 1,
  year: date.getFullYear(),
}))

export const dateRangeForMonth = (month: TimelineMonth): DateRange => {
  const dateTime = DateTime.fromFormat(month.toString(), "yyyyMM")
  return ({
    startDateLocal: toTimelineDate(dateTime.startOf("month")),
    endDateLocal: toTimelineDate(dateTime.endOf("month")),
  })
}

export const monthTimelineDates = (month: TimelineMonth): TimelineDate[] => {
  const { startDateLocal, endDateLocal } = dateRangeForMonth(month)
  // range#to is exclusive, so we need to add it to the end
  return [...range(startDateLocal, endDateLocal), endDateLocal]
}

export const getDurationStr = (seconds: number): string => {
  let result = ""
  const divider = ":"
  const zeroValue = "0"
  const [h, m, s] = Duration.fromObject({ seconds }).toFormat(`h${divider}m${divider}s`).split(divider)
  const hasHours = h !== zeroValue
  const hasMinutes = m !== zeroValue
  if (hasHours) {
    result += `${h}h`
  }
  if (hasMinutes) {
    const spacer = hasHours ? " " : ""
    result += `${spacer}${m}m`
  }
  if (s !== zeroValue) {
    const spacer = (hasHours || hasMinutes) ? " " : ""
    result += `${spacer}${s}s`
  }
  return result
}

export const formatDuration = (seconds: number): string => {
  const totalMinutes = seconds / 60
  
  if (totalMinutes >= 60) {
    const hours = totalMinutes / 60
    // If it's a whole hour, show without decimals
    if (hours % 1 === 0) {
      return `${Math.round(hours)}h`
    }
    // Otherwise, show with minimal decimal places (remove trailing zeros)
    return `${parseFloat(hours.toFixed(2))}h`
  }
  
  if (totalMinutes >= 10) {
    return `${Math.round(totalMinutes)}m`
  }
  
  const minutes = Math.floor(totalMinutes)
  const remainingSeconds = seconds % 60
  return `${minutes}m ${remainingSeconds}s`
}

/**
 * Returns the Friday of the previous week relative to the given date.
 * Always navigates to the previous week regardless of the current day.
 */
export const getPrevWeekFriday = (date: DateTime): TimelineDate => {
  const FRIDAY_INDEX = 4 // Friday is 4 days from Monday (start of week)
  const prevWeekFriday = date.minus({ weeks: 1 }).startOf("week").plus({ days: FRIDAY_INDEX })
  return toTimelineDate(prevWeekFriday)
}

/**
 * Returns the Monday of the next week relative to the given date.
 * Always navigates to the next week regardless of the current day.
 */
export const getNextWeekMonday = (date: DateTime): TimelineDate => {
  const nextWeekMonday = date.plus({ weeks: 1 }).startOf("week")
  return toTimelineDate(nextWeekMonday)
}
